// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
import type { TriggerQuestion } from "./types";
import { ASBESTOS, OCCUPIED, WAH, UNDERGROUND_SERVICES, ACCESS_OPTIONS, RESCUE_Q, PRE2000_Q } from "./shared";

export const ELECTRICAL_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "electrical-installation": [
    {
      key: "isolationRecord",
      label: "Will safe isolation be recorded — per-operative lock-off and proved dead with a GS38 device at the point of work?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Safe isolation recorded: each operative applies a personal lock-off and caution tag, and circuits are proved dead with a GS38-compliant two-pole indicator at the point of work before any work begins." },
        no: { failCheck: "Per-operative lock-off and prove-dead procedure confirmed" },
      },
      notSure: { failCheck: "Per-operative lock-off and prove-dead procedure confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "isolationOwner",
            label: "Who holds the keys / owns the isolation register?",
            kind: "text",
            placeholder: "e.g. each operative retains their own key; register at the board",
            requiredCheck: "Isolation key-holder / register owner recorded",
            effects: { "*": { scopeLine: "Isolation control: {answer}." } },
          },
        ],
      },
    },
    {
      key: "energisation",
      label: "How will the installation be energised?",
      kind: "choice",
      options: [
        "Separate authorised event after dead testing (by us)",
        "Handed over locked-off for another party to energise",
      ],
      effects: {
        "Separate authorised event after dead testing (by us)": { scopeLine: "Energisation is a separate, authorised event carried out by the competent person only after the BS 7671 dead-test results are satisfactory." },
        "Handed over locked-off for another party to energise": { scopeLine: "The installation is handed over locked-off; energisation is carried out by others under their own authorisation, outside this dead-working method.", sequenceOff: [8] },
      },
    },
    {
      key: "hiddenServices",
      label: "Will you drill or chase into walls, floors or ceilings that may contain hidden services?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-electrical-contact-drilling-into-hidden-service-during-installation"] },
        no: {
          hazardsOff: ["haz-electrical-contact-drilling-into-hidden-service-during-installation"],
          scopeLine: "No drilling or chasing into surfaces that may contain hidden services — fixings are surface-mounted to clear positions only.",
        },
      },
      notSure: { hazardsOn: ["haz-electrical-contact-drilling-into-hidden-service-during-installation"], failCheck: "Drilling into surfaces with possible hidden services confirmed" },
    },
    PRE2000_Q([ASBESTOS], [ASBESTOS]),
    {
      key: "height",
      label: "Is work at height required (high-level containment, risers or ceiling voids)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [WAH] },
        no: { hazardsOff: [WAH], scopeLine: "No work at height — all installation work is at or near floor level." },
      },
      notSure: { hazardsOn: [WAH], failCheck: "Work-at-height requirement confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accessMethod",
            label: "What access equipment will be used?",
            kind: "choice",
            options: ACCESS_OPTIONS,
            effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Access for work at height: ${o}.` }])),
          },
          RESCUE_Q,
        ],
      },
    },
  ],

  "working-near-live-services": [
    {
      key: "liveJustified",
      label: "Is the EAWR 1989 reg 14 three-condition test satisfied for this specific live task?",
      kind: "yesno",
      effects: {
        no: { failCheck: "Regulation 14 three-condition justification confirmed — otherwise the work must be done dead" },
      },
      notSure: { failCheck: "Regulation 14 three-condition justification confirmed — otherwise the work must be done dead" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "liveJustification",
            label: "Justification for live working (EAWR 1989 reg 14)",
            kind: "text",
            placeholder: "Why is it unreasonable to work dead, and reasonable to work live here?",
            requiredCheck: "Reg 14 live-working justification recorded for this task",
            effects: { "*": { scopeLine: "Live working justification (EAWR 1989 reg 14): {answer}. A task-specific live-working permit names the authorised person." } },
          },
        ],
      },
    },
    {
      key: "liveTaskType",
      label: "What is the nature of the live task?",
      kind: "choice",
      options: ["Fault diagnosis / live testing", "Work adjacent to live switchgear", "Work on live conductors"],
      effects: {
        "Fault diagnosis / live testing": { scopeLine: "Live task: fault diagnosis / live testing using GS38 fused test equipment under a live-working permit." },
        "Work adjacent to live switchgear": { scopeLine: "Live task: work immediately adjacent to live switchgear that cannot be isolated, with adjacent live parts shrouded or barriered." },
        "Work on live conductors": { scopeLine: "Live task: work on live conductors — the highest-risk case; insulated tools, single-hand technique and an accompanying person trained in emergency release apply." },
      },
    },
    {
      key: "accompanied",
      label: "Will an accompanying person trained in emergency release and CPR be present throughout?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Live work is never carried out alone — an accompanying person trained in emergency release and CPR is present, with the isolation point they can operate to break contact." },
        no: { failCheck: "Trained accompanying person for live working confirmed" },
      },
      notSure: { failCheck: "Trained accompanying person for live working confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "accompanyOwner",
            label: "Who is the accompanying person and what isolation can they operate?",
            kind: "text",
            placeholder: "e.g. J Smith — can operate the main switch at DB-2",
            requiredCheck: "Accompanying person and emergency isolation point named",
            effects: { "*": { scopeLine: "Accompanying person / emergency isolation: {answer}." } },
          },
        ],
      },
    },
    {
      key: "arcFlash",
      label: "Has the arc-flash incident energy been assessed and arc-rated PPE selected to it?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Arc-flash incident energy assessed at the working position; arc-rated PPE selected to the assessed level." },
        no: { failCheck: "Arc-flash assessment and arc-rated PPE selection confirmed" },
      },
      notSure: { failCheck: "Arc-flash assessment and arc-rated PPE selection confirmed" },
    },
    {
      key: "energisedTesting",
      label: "Does the task involve energised testing?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Sanction-to-test where energised testing is involved"], scopeLine: "Energised testing is involved — a sanction-to-test is issued and GS38 fused test equipment is used." },
        no: { permitsRemove: ["Sanction-to-test where energised testing is involved"], hazardsOff: ["haz-electrical-contact-test-probe-slip-or-unfused-test-lead"], scopeLine: "No energised testing — work is limited to the live task on the permit, with no instrument testing of live parts." },
      },
      notSure: { hazardsOn: ["haz-electrical-contact-test-probe-slip-or-unfused-test-lead"], permitsAdd: ["Sanction-to-test where energised testing is involved"], failCheck: "Energised-testing requirement confirmed" },
    },
    {
      key: "occupied",
      label: "Are the premises occupied or is the public nearby during the live work?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Premises unoccupied with no public nearby — the live zone is within a controlled work area only." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy / public proximity during live work confirmed" },
    },
  ],

  "temporary-site-electrics": [
    {
      key: "reducedVoltage",
      label: "Are all portable tools and task lighting fed from 110V centre-tapped-to-earth (or cordless)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "All portable tools and task lighting are fed from 110V centre-tapped-to-earth (or cordless), limiting the voltage to earth to 55V; any unavoidable 230V circuit is RCD-protected." },
        no: { failCheck: "110V CTE (or cordless) supply for all portable tools confirmed" },
      },
      notSure: { failCheck: "110V CTE (or cordless) supply for all portable tools confirmed" },
    },
    {
      key: "inspectionRegime",
      label: "Is the inspection regime defined — weekly visual plus three-monthly periodic inspection and test?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Inspection regime: weekly visual inspection of boards, leads, transformers and generator, plus periodic inspection and test at intervals no greater than three months, recorded in the site register." },
        no: { failCheck: "Temporary-installation inspection regime confirmed" },
      },
      notSure: { failCheck: "Temporary-installation inspection regime confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "inspectionOwner",
            label: "Who carries out and records the inspections?",
            kind: "text",
            placeholder: "e.g. site electrician, logged in the temporary-supply register",
            requiredCheck: "Inspection responsibility and register owner recorded",
            effects: { "*": { scopeLine: "Inspection responsibility: {answer}." } },
          },
        ],
      },
    },
    {
      key: "supplyOrigin",
      label: "What is the source of the temporary supply?",
      kind: "choice",
      options: ["Mains origin (DNO supply)", "Mobile generator", "Both mains and standby generator"],
      effects: {
        "Mains origin (DNO supply)": {
          hazardsOff: ["haz-electrical-contact-generator-fumes-and-fuel-handling"],
          substancesOff: ["Generator fuel"],
          emergencyOff: ["Generator carbon monoxide", "Fuel spill"],
          sequenceOff: [4],
          scopeLine: "The temporary supply is taken from a mains origin — generator fuel, fumes and earthing controls are not required for this task.",
        },
        "Mobile generator": { hazardsOn: ["haz-electrical-contact-generator-fumes-and-fuel-handling"], scopeLine: "A mobile generator feeds the site — it is sited in the open, earthed, with bunded fuel storage and RCD-protected outgoing circuits." },
        "Both mains and standby generator": { hazardsOn: ["haz-electrical-contact-generator-fumes-and-fuel-handling"], scopeLine: "Mains origin with a standby generator — generator earthing, fuel bunding and RCD-protected outgoing circuits apply when the generator is in use." },
      },
    },
    {
      key: "weatherproof",
      label: "Are distribution assemblies IP-rated weatherproof and protected from water ingress?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Distribution assemblies and connectors are IP-rated weatherproof, raised clear of standing water and kept under cover with lids and glands closed." },
        no: { hazardsOn: ["haz-electrical-contact-water-ingress-to-temporary-distribution"], failCheck: "Weatherproof / IP-rated distribution equipment confirmed" },
      },
      notSure: { hazardsOn: ["haz-electrical-contact-water-ingress-to-temporary-distribution"], failCheck: "Weatherproof / IP-rated distribution equipment confirmed" },
    },
    {
      key: "buriedCable",
      label: "Will any supply cables be buried or trenched across the site?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Permit to dig"], scopeLine: "Some supply cables are buried — a permit to dig and CAT/Genny check apply where ground is broken." },
        no: { scopeLine: "All supply cables are run overhead on catenaries or surface-ramped — no ground is broken, so a permit to dig is not required for this task." },
      },
      notSure: { failCheck: "Buried-cable / excavation requirement confirmed" },
    },
  ],

  "overhead-power-lines": [
    {
      key: "dnoConsulted",
      label: "Has the line voltage and owner been confirmed with the network operator, and diversion/isolation considered?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Line voltage and owner confirmed with the DNO; diversion or de-energisation considered per GS6 before exclusion-zone controls were adopted." },
        no: { failCheck: "DNO consultation and GS6 diversion/isolation decision confirmed" },
      },
      notSure: { failCheck: "DNO consultation and GS6 diversion/isolation decision confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "exclusionDistance",
            label: "Line voltage and agreed GS6 exclusion distance",
            kind: "text",
            placeholder: "e.g. 11kV, 3 m clearance, goalposts set 6 m out for plant reach",
            requiredCheck: "Line voltage and agreed exclusion distance recorded",
            effects: { "*": { scopeLine: "Overhead line and exclusion set-out: {answer}." } },
          },
        ],
      },
    },
    {
      key: "controlStrategy",
      label: "What is the primary GS6 control for the line?",
      kind: "choice",
      options: ["Diverted or made dead by the DNO", "Live — exclusion zone, goalposts and barriers"],
      effects: {
        "Diverted or made dead by the DNO": { scopeLine: "Primary control: the line is diverted or made dead by the DNO for the works; exclusion-zone controls are retained as a backstop until isolation is confirmed in writing." },
        "Live — exclusion zone, goalposts and barriers": { scopeLine: "The line stays live — control is by GS6 exclusion distances, goalposts, barriers and banksman supervision, with diversion/isolation found not reasonably practicable." },
      },
    },
    {
      key: "crossingPlant",
      label: "Will plant cross beneath the lines, with goalposts and barriers in place?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-plant-equipment-raised-body-above-goalpost-passage-height"], scopeLine: "Plant crosses beneath the lines only at goalposted passage points; goalpost height is set below the GS6 clearance and signposted." },
        no: {
          hazardsOff: ["haz-plant-equipment-raised-body-above-goalpost-passage-height"],
          scopeLine: "No plant crosses beneath the lines — barriers exclude all plant from the no-go zone, so goalposted crossings are not required.",
        },
      },
      notSure: { hazardsOn: ["haz-plant-equipment-raised-body-above-goalpost-passage-height"], failCheck: "Plant-crossing arrangement beneath the lines confirmed" },
    },
    {
      key: "craneJib",
      label: "Will a crane or 360 excavator operate near the lines?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-plant-equipment-jib-swing-into-overhead-conductor-zone"], scopeLine: "Craneage / 360 excavator near the lines — positioned so the jib cannot reach the exclusion zone at any slew angle; limiters set where supported." },
        no: { hazardsOff: ["haz-plant-equipment-jib-swing-into-overhead-conductor-zone"], scopeLine: "No crane or slewing excavator near the lines — no jib can reach the conductor zone." },
      },
      notSure: { hazardsOn: ["haz-plant-equipment-jib-swing-into-overhead-conductor-zone"], failCheck: "Crane / slewing-plant proximity to the lines confirmed" },
    },
    {
      key: "longMaterials",
      label: "Will scaffolding or long materials be handled near the lines?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-plant-equipment-long-material-carried-into-conductor-zone"] },
        no: { hazardsOff: ["haz-plant-equipment-long-material-carried-into-conductor-zone"], scopeLine: "No scaffolding or long materials handled near the lines — material storage and erection are planned outside the exclusion zone." },
      },
      notSure: { hazardsOn: ["haz-plant-equipment-long-material-carried-into-conductor-zone"], failCheck: "Handling of long materials near the lines confirmed" },
    },
    {
      key: "excavationNearby",
      label: "Will any digging be carried out beneath the lines or near pole/pylon supports?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Excavation permit where digging is carried out beneath the lines"] },
        no: {
          hazardsOff: [UNDERGROUND_SERVICES],
          permitsRemove: ["Excavation permit where digging is carried out beneath the lines"],
          scopeLine: "No digging beneath the lines or near supports — underground-services controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Excavation permit where digging is carried out beneath the lines"], failCheck: "Excavation near the lines / supports confirmed" },
    },
    {
      key: "banksman",
      label: "Will a dedicated banksman control every plant approach and crossing?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "A dedicated banksman controls every plant approach and crossing beneath the lines; no high plant enters the zone without banksman supervision." },
        no: { failCheck: "Dedicated banksman for the overhead-line zone confirmed" },
      },
      notSure: { failCheck: "Dedicated banksman for the overhead-line zone confirmed" },
    },
  ],
};
